// @ts-check
import React, { useCallback, useState } from 'react';
import { useQuery } from 'react-query';
import { Button } from 'preferral-ui/components/Button';
import { FAIcon } from 'preferral-ui/components/FAIcon';
import { Table, TableContainer, TH, TD } from 'preferral-ui/components/Table';
import api from 'utils/api';
import { NewReferralTagModal } from './NewReferralTagModal';
import Spinner from 'shared/components/Spinner';
import {
  DropdownButton,
  DropdownItemButton,
} from 'preferral-ui/components/DropdownButton';
import { DeleteReferralTagModal } from './DeleteReferralTagModal';

/** @typedef {
 | {type: "NEW_REFERRAL_TAG"} | {type: "DELETE_REFERRAL_TAG", referralTagId: string}
} ActiveModal */

/** @typedef {{
  params: { entityId: string }
}} ReferralTagsScreenProps */

/** @type { React.FC<ReferralTagsScreenProps> } */
export const ReferralTagsScreen = (props) => {
  const { params } = props;
  const { entityId } = params;

  /** @type { [ActiveModal | null, React.Dispatch<ActiveModal | null>]} */
  const [activeModal, setActiveModal] = useState(null);
  const closeModal = useCallback(() => setActiveModal(null), []);

  const queryKey = `referral-tags-${entityId}`;
  const { error, data, isFetched, refetch } = useQuery(queryKey, () =>
    api
      .get(`/agent_portal/api/organizations/${entityId}/referral_tags`)
      .then((resp) => resp.data)
  );

  return (
    <>
      <NewReferralTagModal
        isOpen={activeModal && activeModal.type === 'NEW_REFERRAL_TAG'}
        organizationId={entityId}
        onClose={closeModal}
        onSuccess={refetch}
      />
      {
        activeModal && activeModal.type === "DELETE_REFERRAL_TAG" ? (
          <DeleteReferralTagModal
            isOpen={activeModal && activeModal.type === "DELETE_REFERRAL_TAG"}
            referralTagId={activeModal.referralTagId}
            onClose={closeModal}
            onSuccess={() => {
              refetch()
              closeModal()
            }}
          />
        ) : null
      }
      <div className="_ReferralTagsScreen pb-16">
        {!isFetched ? (
          <div className="p-12 text-center">
            <Spinner />
          </div>
        ) : error || !data ? (
          <p>Failed to load.</p>
        ) : (
          <>
            <div className="flex items-end justify-between">
              <div />
              <Button
                type="button"
                color="blue"
                kind="primary"
                size="sm"
                onClick={() => setActiveModal({ type: 'NEW_REFERRAL_TAG' })}
              >
                <span className="mr-2">
                  <FAIcon icon="plus" />
                </span>
                New Referral Tag
              </Button>
            </div>
            <div className="mt-4">
              <TableContainer>
                <Table>
                  <thead>
                    <tr>
                      <TH>Label</TH>
                      <TH>Description</TH>
                      <TH />
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {data.referralTags.map((referralTag) => (
                      <tr key={referralTag.id}>
                        <TD>
                          <div className="flex items-center">
                            <div
                              style={{ backgroundColor: referralTag.color }}
                              className="w-8 h-8 rounded-full shadow-inner"
                            />
                            <p className="ml-4 font-semibold">
                              {referralTag.label}
                            </p>
                          </div>
                        </TD>
                        <TD>{referralTag.description}</TD>
                        <TD>
                          <DropdownButton label="Actions">
                            <DropdownItemButton color="red" onClick={() => setActiveModal({ type: "DELETE_REFERRAL_TAG", referralTagId: referralTag.id })}>
                              Delete Tag
                            </DropdownItemButton>
                          </DropdownButton>
                        </TD>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableContainer>
            </div>
            {/* <pre>{JSON.stringify(data, null, 2)}</pre> */}
          </>
        )}
      </div>
    </>
  );
};
